import EditorjsList from "@editorjs/list";
import EditorjsImage from "@editorjs/image";

export interface BannerToolData {
  image?: {
    url: string;
    alt: string;
  };
  linkTitle: string;
  title: string;
  description: string;
  link: string;
}

export class BannerTool {
  data: BannerToolData;
  wrapper: HTMLElement | undefined;
  previewContainer: HTMLElement | undefined;

  constructor({ data }: { data: Partial<BannerToolData> }) {
    this.data = {
      image: data.image?.url ? { url: data.image.url, alt: data.image.alt || "" } : undefined,
      title: data.title || "",
      description: data.description || "",
      link: data.link || "",
      linkTitle: data.linkTitle || "Watch Now"
    };
  }

  static get toolbox() {
    return {
      title: "Banner",
      icon: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-newspaper-icon lucide-newspaper"><path d="M15 18h-5"/><path d="M18 14h-8"/><path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-4 0v-9a2 2 0 0 1 2-2h2"/><rect width="8" height="4" x="10" y="6" rx="1"/></svg>'
    };
  }

  render() {
    this.wrapper = document.createElement("div");
    this.wrapper.classList.add("cdx-block");

    // Add styles
    const style = document.createElement("style");
    this.wrapper.appendChild(style);

    this.previewContainer = document.createElement("div");
    this.previewContainer.addEventListener("click", () => this.openModal());

    this.updatePreview();
    this.wrapper.appendChild(this.previewContainer);

    // Auto-open modal if it's a new block (empty data)
    if (!this.data.image?.url && !this.data.title) {
      setTimeout(() => this.openModal(), 100);
    }

    return this.wrapper;
  }

  updatePreview() {
    if (!this.previewContainer) return;

    this.previewContainer.style.border = "none";
    this.previewContainer.style.padding = "0";
    this.previewContainer.style.backgroundColor = "transparent";
    this.previewContainer.style.minHeight = "auto";
    this.previewContainer.style.display = "block";
    this.previewContainer.style.cursor = "pointer";

    const imageHtml = this.data.image?.url
      ? `<img class='mt-0!' src="${this.data.image.url}" alt="${
          this.data.image.alt || this.data.title
        }" loading="lazy" decoding="async" style="max-width: 100%; border-radius: 12px; object-fit: cover; aspect-ratio: unset !important; width: 100% !important;"/>`
      : "";

    this.previewContainer.innerHTML = `
        <div class='banner-wrapper'>
          ${imageHtml}
          <div class='banner-content'>
             <h3 class='font-poppins! font-bold! text-[24px]! text-white! text-center md:text-[36px]! m-0! leading-[100%]'>${this.data.title}</p>
             <p class='font-poppins! font-normal! text-[16px]! text-white! leading-[140%]! text-center m-0! mb-7.5!'>${this.data.description}</p>
             <a class='font-poppins! bunner-button leading-[100%]' href="${this.data.link}">${this.data.linkTitle}</a>
          </div>
        </div>
      `;

    const link = this.previewContainer.querySelector("a");
    if (link) {
      link.addEventListener("click", (e) => {
        e.preventDefault();
      });
    }
  }

  openModal() {
    // Create modal overlay
    const overlay = document.createElement("div");
    overlay.style.position = "fixed";
    overlay.style.top = "0";
    overlay.style.left = "0";
    overlay.style.width = "100%";
    overlay.style.height = "100%";
    overlay.style.backgroundColor = "rgba(0, 0, 0, 0.5)";
    overlay.style.zIndex = "9999";
    overlay.style.display = "flex";
    overlay.style.alignItems = "center";
    overlay.style.justifyContent = "center";
    overlay.style.backdropFilter = "blur(2px)";

    // Create modal content
    const modal = document.createElement("div");
    modal.style.backgroundColor = "white";
    modal.style.padding = "20px";
    modal.style.borderRadius = "8px";
    modal.style.width = "500px";
    modal.style.maxWidth = "90%";
    modal.style.maxHeight = "90vh";
    modal.style.overflowY = "auto";
    modal.style.display = "flex";
    modal.style.flexDirection = "column";
    modal.style.gap = "15px";
    modal.style.boxShadow = "0 4px 6px rgba(0, 0, 0, 0.1)";

    // Styles for inputs
    const style = document.createElement("style");
    style.textContent = `
      .banner-tool-input::placeholder {
        color: #9F9F9F;
        opacity: 1;
      }
    `;
    modal.appendChild(style);

    const tempData: BannerToolData = {
      ...this.data,
      image: this.data.image ? { ...this.data.image } : undefined
    };

    // Image Area (Drag & Drop)
    const imageContainer = document.createElement("div");
    imageContainer.style.backgroundColor = "#f5f5f5";
    imageContainer.style.borderRadius = "6px";
    imageContainer.style.minHeight = "150px";
    imageContainer.style.display = "flex";
    imageContainer.style.alignItems = "center";
    imageContainer.style.justifyContent = "center";
    imageContainer.style.cursor = "pointer";
    imageContainer.style.overflow = "hidden";
    imageContainer.style.position = "relative";
    imageContainer.style.border = "2px dashed transparent";
    imageContainer.style.transition = "all 0.2s";

    const renderModalImage = () => {
      imageContainer.innerHTML = "";
      if (tempData.image?.url) {
        const img = document.createElement("img");
        img.src = tempData.image.url;
        img.style.width = "100%";
        img.style.height = "auto";
        img.style.objectFit = "cover";
        imageContainer.appendChild(img);
      } else {
        const placeholder = document.createElement("div");
        placeholder.innerText = "Click or Drop image here";
        placeholder.style.color = "#9F9F9F";
        imageContainer.appendChild(placeholder);
      }
    };
    renderModalImage();

    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = "image/*";
    fileInput.style.display = "none";
    fileInput.addEventListener("change", (event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (file) {
        const url = URL.createObjectURL(file);
        tempData.image = { url, alt: tempData.image?.alt || "" };
        renderModalImage();
      }
    });

    imageContainer.addEventListener("click", () => fileInput.click());

    // Drag and Drop
    const handleDragEnter = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      imageContainer.style.backgroundColor = "#e8f4fd";
      imageContainer.style.borderColor = "#2196F3";
    };

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      imageContainer.style.backgroundColor = "#f5f5f5";
      imageContainer.style.borderColor = "transparent";
    };

    const handleDrop = async (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      imageContainer.style.backgroundColor = "#f5f5f5";
      imageContainer.style.borderColor = "transparent";

      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        const file = files[0];
        if (file?.type.startsWith("image/")) {
          const url = URL.createObjectURL(file);
          tempData.image = { url, alt: tempData.image?.alt || "" };
          renderModalImage();
          return;
        }
      }

      const urlData = e.dataTransfer?.getData("text/uri-list");
      if (urlData) {
        tempData.image = { url: urlData, alt: tempData.image?.alt || "" };
        renderModalImage();
      }
    };

    imageContainer.addEventListener("dragenter", handleDragEnter);
    imageContainer.addEventListener("dragover", handleDragOver);
    imageContainer.addEventListener("dragleave", handleDragLeave);
    imageContainer.addEventListener("drop", handleDrop);

    modal.appendChild(imageContainer);

    // Inputs
    const createInput = (placeholder: string, key: "title" | "description" | "link" | "linkTitle" | "alt") => {
      const input = document.createElement("input");
      input.classList.add("cdx-input", "banner-tool-input");
      input.placeholder = placeholder;
      input.value = (key === "alt" ? tempData.image?.alt : tempData[key]) || "";
      input.style.width = "100%";
      input.style.padding = "10px";
      input.style.border = "1px solid #e0e0e0";
      input.style.borderRadius = "4px";
      input.addEventListener("input", (e) => {
        const value = (e.target as HTMLInputElement).value;
        if (key === "alt") {
          if (tempData.image) {
            tempData.image.alt = value;
          } else {
            tempData.image = { url: "", alt: value };
          }
        } else {
          tempData[key] = value;
        }
      });
      return input;
    };

    modal.appendChild(createInput("Image Alt", "alt"));
    modal.appendChild(createInput("Title", "title"));
    modal.appendChild(createInput("Description", "description"));
    modal.appendChild(createInput("Link URL", "link"));
    modal.appendChild(createInput("Link Title", "linkTitle"));

    // Buttons
    const buttonContainer = document.createElement("div");
    buttonContainer.style.display = "flex";
    buttonContainer.style.justifyContent = "flex-end";
    buttonContainer.style.marginTop = "10px";

    const closeModal = () => {
      document.body.removeChild(overlay);
    };

    const saveButton = document.createElement("button");
    saveButton.innerText = "Save";
    saveButton.style.padding = "8px 16px";
    saveButton.style.border = "none";
    saveButton.style.borderRadius = "4px";
    saveButton.style.backgroundColor = "#4CAF50";
    saveButton.style.color = "white";
    saveButton.style.cursor = "pointer";
    saveButton.addEventListener("click", () => {
      // Only keep image if it has a URL
      if (tempData.image && !tempData.image.url) {
        tempData.image = undefined;
      }
      this.data = tempData;
      this.updatePreview();
      document.body.removeChild(overlay);
    });

    buttonContainer.appendChild(saveButton);
    modal.appendChild(buttonContainer);

    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    // Close on click outside
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) {
        closeModal();
      }
    });
  }

  save() {
    return this.data;
  }
}

interface FAQItem {
  title: string;
  content: string;
}

export interface FAQToolData {
  items: FAQItem[];
}

export class FAQTool {
  data: FAQToolData;
  wrapper: HTMLElement | undefined;

  constructor({ data }: { data: Partial<FAQToolData> }) {
    this.data = {
      items: data.items || [{ title: "", content: "" }]
    };
  }

  static get toolbox() {
    return {
      title: "FAQ",
      icon: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-question-mark-icon lucide-circle-question-mark"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>'
    };
  }

  render() {
    this.wrapper = document.createElement("div");
    this.wrapper.classList.add("cdx-block");

    const container = document.createElement("div");
    container.style.border = "1px solid #e0e0e0";
    container.style.borderRadius = "8px";
    container.style.padding = "15px";
    container.style.display = "flex";
    container.style.flexDirection = "column";
    container.style.gap = "15px";

    const itemsContainer = document.createElement("div");
    itemsContainer.style.display = "flex";
    itemsContainer.style.flexDirection = "column";
    itemsContainer.style.gap = "10px";

    const renderFAQItem = (item: FAQItem, index: number) => {
      const itemContainer = document.createElement("div");
      itemContainer.style.border = "1px solid #f0f0f0";
      itemContainer.style.borderRadius = "6px";
      itemContainer.style.padding = "10px";
      itemContainer.style.display = "flex";
      itemContainer.style.flexDirection = "column";
      itemContainer.style.gap = "8px";

      const titleInput = document.createElement("input");
      titleInput.classList.add("cdx-input");
      titleInput.placeholder = "Title";
      titleInput.value = item.title || "";
      titleInput.style.width = "100%";
      titleInput.addEventListener("input", (e) => {
        if (this.data.items[index]) {
          this.data.items[index].title = (e.target as HTMLInputElement).value;
        }
      });

      const contentInput = document.createElement("textarea");
      contentInput.classList.add("cdx-input");
      contentInput.placeholder = "Content";
      contentInput.value = item.content || "";
      contentInput.style.width = "100%";
      contentInput.style.minHeight = "60px";
      contentInput.style.resize = "vertical";
      contentInput.addEventListener("input", (e) => {
        if (this.data.items[index]) {
          this.data.items[index].content = (e.target as HTMLTextAreaElement).value;
        }
      });

      const removeButton = document.createElement("button");
      removeButton.innerText = "Remove";
      removeButton.style.backgroundColor = "#E24A4A";
      removeButton.style.color = "white";
      removeButton.style.border = "none";
      removeButton.style.borderRadius = "4px";
      removeButton.style.padding = "5px 10px";
      removeButton.style.cursor = "pointer";
      removeButton.style.alignSelf = "flex-end";
      removeButton.addEventListener("click", () => {
        this.data.items.splice(index, 1);
        renderAllItems();
      });

      itemContainer.appendChild(titleInput);
      itemContainer.appendChild(contentInput);
      itemContainer.appendChild(removeButton);

      return itemContainer;
    };

    const renderAllItems = () => {
      itemsContainer.innerHTML = "";
      this.data.items.forEach((item, index) => {
        itemsContainer.appendChild(renderFAQItem(item, index));
      });
    };

    const addButton = document.createElement("button");
    addButton.innerText = "Add";
    addButton.style.backgroundColor = "#fbfbfb";
    addButton.style.color = "#21272e";
    addButton.style.color = "#21272e";
    addButton.style.border = "1px solid #e0e0e0";
    addButton.style.borderRadius = "4px";
    addButton.style.padding = "8px 16px";
    addButton.style.cursor = "pointer";
    addButton.addEventListener("click", () => {
      this.data.items.push({ title: "", content: "" });
      renderAllItems();
    });

    renderAllItems();
    container.appendChild(itemsContainer);
    container.appendChild(addButton);

    if (this.wrapper) {
      this.wrapper.appendChild(container);
    }

    return this.wrapper;
  }

  save() {
    return this.data;
  }
}

export class CustomList extends EditorjsList {
  override renderSettings() {
    const settings = super.renderSettings();

    // Filter out the checklist option, keep everything else
    return settings.filter((item) => {
      if ("label" in item && typeof item.label === "string") {
        return item.label !== "Checklist";
      }
    });
  }
}

export class CustomImage extends EditorjsImage {
  private customData: EditorjsImage["data"] & { alt: string };

  // @ts-expect-error - EditorJS typings are incomplete
  constructor(tool) {
    super(tool);
    this.customData = tool.data || {};
  }

  override render() {
    const container = super.render();

    const input = document.createElement("input");
    input.classList.add("cdx-input");
    input.placeholder = "Alt text";
    input.value = this.customData.alt || "";
    input.style.marginTop = "10px";
    input.style.width = "100%";
    input.style.fontSize = "13px";
    input.style.padding = "10px";
    input.style.border = "1px solid #e0e0e0";
    input.style.borderRadius = "6px";
    input.style.color = "#707684";

    input.addEventListener("input", (e) => {
      this.customData.alt = (e.target as HTMLInputElement).value;
    });

    container.appendChild(input);

    return container;
  }

  override save() {
    const data: EditorjsImage["data"] & { alt: string } = super.save();
    data.alt = this.customData.alt || "";
    return data;
  }
}

export interface CTAButtonToolData {
  text: string;
  link: string;
}

export class CTAButtonTool {
  data: CTAButtonToolData;
  wrapper: HTMLElement | undefined;
  previewContainer: HTMLElement | undefined;

  constructor({ data }: { data: Partial<CTAButtonToolData> }) {
    this.data = {
      text: data.text || "Try Now",
      link: data.link || ""
    };
  }

  static get toolbox() {
    return {
      title: "CTA Button",
      icon: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="8" width="18" height="8" rx="2"/><path d="M12 12h.01"/></svg>'
    };
  }

  render() {
    this.wrapper = document.createElement("div");
    this.wrapper.classList.add("cdx-block");

    this.previewContainer = document.createElement("div");
    this.previewContainer.addEventListener("click", () => this.openModal());

    this.updatePreview();
    this.wrapper.appendChild(this.previewContainer);

    // Auto-open modal if it's a new block (empty link)
    if (!this.data.link) {
      setTimeout(() => this.openModal(), 100);
    }

    return this.wrapper;
  }

  updatePreview() {
    if (!this.previewContainer) return;

    this.previewContainer.style.cursor = "pointer";
    this.previewContainer.style.display = "block";

    this.previewContainer.innerHTML = `
      <div style="display: flex; justify-content: center; padding: 10px 0;">
        <a href="${this.data.link}" 
           class="cta-button-preview" 
           style="
             box-shadow: 0 8px 35px 0 rgba(0, 0, 0, 0.14);
             color: #ffffff;
             background: #005EE8;
             border-color: #005EE8;
             text-align: center;
             width: 180px;
             padding: 10px;
             border-radius: 60px;
             display: inline-flex;
             align-items: center;
             justify-content: center;
             text-decoration: underline;
             font-family: inherit;
             font-size: 14px;
             font-weight: 500;
           ">${this.data.text}</a>
      </div>
    `;

    const link = this.previewContainer.querySelector("a");
    if (link) {
      link.addEventListener("click", (e) => {
        e.preventDefault();
      });
    }
  }

  openModal() {
    // Create modal overlay
    const overlay = document.createElement("div");
    overlay.style.position = "fixed";
    overlay.style.top = "0";
    overlay.style.left = "0";
    overlay.style.width = "100%";
    overlay.style.height = "100%";
    overlay.style.backgroundColor = "rgba(0, 0, 0, 0.5)";
    overlay.style.zIndex = "9999";
    overlay.style.display = "flex";
    overlay.style.alignItems = "center";
    overlay.style.justifyContent = "center";
    overlay.style.backdropFilter = "blur(2px)";

    // Create modal content
    const modal = document.createElement("div");
    modal.style.backgroundColor = "white";
    modal.style.padding = "20px";
    modal.style.borderRadius = "8px";
    modal.style.width = "400px";
    modal.style.maxWidth = "90%";
    modal.style.maxHeight = "90vh";
    modal.style.overflowY = "auto";
    modal.style.display = "flex";
    modal.style.flexDirection = "column";
    modal.style.gap = "15px";
    modal.style.boxShadow = "0 4px 6px rgba(0, 0, 0, 0.1)";

    // Modal title
    const title = document.createElement("h3");
    title.textContent = "CTA Button Settings";
    title.style.margin = "0";
    title.style.fontSize = "16px";
    title.style.fontWeight = "600";
    title.style.color = "#333";
    modal.appendChild(title);

    // Styles for inputs
    const style = document.createElement("style");
    style.textContent = `
      .cta-tool-input::placeholder {
        color: #9F9F9F;
        opacity: 1;
      }
    `;
    modal.appendChild(style);

    const tempData: CTAButtonToolData = { ...this.data };

    // Create inputs
    const createInput = (placeholder: string, key: keyof CTAButtonToolData) => {
      const input = document.createElement("input");
      input.classList.add("cdx-input", "cta-tool-input");
      input.placeholder = placeholder;
      input.value = tempData[key] || "";
      input.style.width = "100%";
      input.style.padding = "10px";
      input.style.border = "1px solid #e0e0e0";
      input.style.borderRadius = "4px";
      input.style.boxSizing = "border-box";
      input.addEventListener("input", (e) => {
        tempData[key] = (e.target as HTMLInputElement).value;
      });
      return input;
    };

    modal.appendChild(createInput("Button Text", "text"));
    modal.appendChild(createInput("Link URL (e.g., https://example.com)", "link"));

    // Buttons
    const buttonContainer = document.createElement("div");
    buttonContainer.style.display = "flex";
    buttonContainer.style.justifyContent = "flex-end";
    buttonContainer.style.gap = "10px";
    buttonContainer.style.marginTop = "10px";

    const closeModal = () => {
      document.body.removeChild(overlay);
    };

    const saveButton = document.createElement("button");
    saveButton.innerText = "Save";
    saveButton.style.padding = "8px 16px";
    saveButton.style.border = "none";
    saveButton.style.borderRadius = "4px";
    saveButton.style.backgroundColor = "#4CAF50";
    saveButton.style.color = "white";
    saveButton.style.cursor = "pointer";
    saveButton.addEventListener("click", () => {
      this.data = tempData;
      this.updatePreview();
      closeModal();
    });

    buttonContainer.appendChild(saveButton);
    modal.appendChild(buttonContainer);

    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    // Close on click outside
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) {
        closeModal();
      }
    });
  }

  save() {
    return this.data;
  }
}

export interface ProsConsToolData {
  pros: string[];
  cons: string[];
}

export class ProsConsTool {
  data: ProsConsToolData;
  wrapper: HTMLElement | undefined;
  previewContainer: HTMLElement | undefined;

  constructor({ data }: { data: Partial<ProsConsToolData> }) {
    this.data = {
      pros: data.pros || [],
      cons: data.cons || []
    };
  }

  static get toolbox() {
    return {
      title: "Pros & Cons",
      icon: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-scale-icon lucide-scale"><path d="M12 3v18"/><path d="m19 8 3 8a5 5 0 0 1-6 0zV7"/><path d="M3 7h1a17 17 0 0 0 8-2 17 17 0 0 0 8 2h1"/><path d="m5 8 3 8a5 5 0 0 1-6 0zV7"/><path d="M7 21h10"/></svg>'
    };
  }

  render() {
    this.wrapper = document.createElement("div");
    this.wrapper.classList.add("cdx-block");

    this.previewContainer = document.createElement("div");

    this.updatePreview();
    this.wrapper.appendChild(this.previewContainer);

    return this.wrapper;
  }

  updatePreview() {
    if (!this.previewContainer) return;

    this.previewContainer.innerHTML = `
      <div style="display: flex;">
        <div style="flex: 1;">
          <div style="background-color: #02bc7d; color: white; padding: 12px 25px; justify-content: center; align-items: center; display: flex; font-size: 18px; font-weight: 600; gap: 5px; line-height: 1.3;">
            Pros
          </div>
          <div style="background-color: #e6f7ee; padding: 15px; display: flex; flex-direction: column;">
            <div id="pros-container"></div>
            <button id="add-pros" style="background-color: white; border: 1px solid #e0e0e0; padding: 5px 10px; margin-top: 10px; cursor: pointer; color: #1d202b; border-radius: 7px;">Add Pro</button>
          </div>
        </div>
        <div style="flex: 1;">
          <div style="background-color: #f44336; color: white; padding: 12px 25px; justify-content: center; align-items: center; display: flex; font-size: 18px; font-weight: 600; gap: 5px; line-height: 1.3;">
            Cons
          </div>
          <div style="background-color: #ffeeed; padding: 15px; display: flex; flex-direction: column;">
            <div id="cons-container"></div>
             <button id="add-cons" style="background-color: white; border: 1px solid #e0e0e0; padding: 5px 10px; margin-top: 10px; cursor: pointer; color: #1d202b; border-radius: 7px;">Add Con</button>
          </div>
        </div>
      </div>
    `;

    this.renderEditableLists();
  }

  renderEditableLists() {
    const prosContainer = this.previewContainer?.querySelector("#pros-container") as HTMLElement;
    const consContainer = this.previewContainer?.querySelector("#cons-container") as HTMLElement;

    if (prosContainer) {
      prosContainer.innerHTML = "";
      this.data.pros.forEach((pro, index) => {
        const itemRow = document.createElement("div");
        itemRow.style.display = "flex";
        itemRow.style.gap = "5px";
        itemRow.style.marginBottom = "5px";

        const input = document.createElement("input");
        input.type = "text";
        input.value = pro;
        input.style.flex = "1";
        input.style.padding = "5px";
        input.style.border = "1px solid #e0e0e0";
        input.style.borderRadius = "7px";
        input.addEventListener("input", (e) => {
          this.data.pros[index] = (e.target as HTMLInputElement).value;
        });

        const removeButton = document.createElement("button");
        removeButton.textContent = "×";
        removeButton.style.backgroundColor = "white";
        removeButton.style.color = "#1d202b";
        removeButton.style.border = "1px solid #e0e0e0";
        removeButton.style.width = "32px";
        removeButton.style.borderRadius = "7px";
        removeButton.style.height = "32px";
        removeButton.style.cursor = "pointer";
        removeButton.addEventListener("click", () => {
          this.data.pros.splice(index, 1);
          this.updatePreview();
        });

        itemRow.appendChild(input);
        itemRow.appendChild(removeButton);
        prosContainer.appendChild(itemRow);
      });
    }

    if (consContainer) {
      consContainer.innerHTML = "";
      this.data.cons.forEach((con, index) => {
        const itemRow = document.createElement("div");
        itemRow.style.display = "flex";
        itemRow.style.gap = "5px";
        itemRow.style.marginBottom = "5px";

        const input = document.createElement("input");
        input.type = "text";
        input.value = con;
        input.style.flex = "1";
        input.style.padding = "5px";
        input.style.border = "1px solid #e0e0e0";
        input.style.borderRadius = "7px";
        input.addEventListener("input", (e) => {
          this.data.cons[index] = (e.target as HTMLInputElement).value;
        });

        const removeButton = document.createElement("button");
        removeButton.textContent = "×";
        removeButton.style.backgroundColor = "white";
        removeButton.style.color = "#1d202b";
        removeButton.style.border = "1px solid #e0e0e0";
        removeButton.style.width = "32px";
        removeButton.style.borderRadius = "7px";
        removeButton.style.height = "32px";
        removeButton.style.cursor = "pointer";
        removeButton.addEventListener("click", () => {
          this.data.cons.splice(index, 1);
          this.updatePreview();
        });

        itemRow.appendChild(input);
        itemRow.appendChild(removeButton);
        consContainer.appendChild(itemRow);
      });
    }

    const addProsButton = this.previewContainer?.querySelector("#add-pros") as HTMLButtonElement;
    const addConsButton = this.previewContainer?.querySelector("#add-cons") as HTMLButtonElement;

    addProsButton?.addEventListener("click", () => {
      this.data.pros.push("");
      this.updatePreview();
    });

    addConsButton?.addEventListener("click", () => {
      this.data.cons.push("");
      this.updatePreview();
    });
  }

  save() {
    return this.data;
  }
}

export interface HtmlToolData {
  htmlContent: string;
}

export class HtmlTool {
  data: HtmlToolData;
  wrapper: HTMLElement | undefined;
  textarea: HTMLTextAreaElement | undefined;
  preview: HTMLElement | undefined;

  constructor({ data }: { data: Partial<HtmlToolData> }) {
    this.data = { htmlContent: data.htmlContent || "" };
  }

  static get toolbox() {
    return {
      title: "HTML",
      icon: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m8 3 4 8 4-8"/><path d="M4 14h16"/><path d="M14 14l-4 8-4-8"/></svg>'
    };
  }

  render() {
    this.wrapper = document.createElement("div");
    this.wrapper.classList.add("cdx-block");
    this.wrapper.style.padding = "10px 0";

    const container = document.createElement("div");
    container.style.display = "flex";
    container.style.flexDirection = "column";
    container.style.gap = "10px";

    this.textarea = document.createElement("textarea");
    this.textarea.classList.add("cdx-input");
    this.textarea.value = this.data.htmlContent;
    this.textarea.placeholder = "Enter raw HTML here...";
    this.textarea.rows = 6;
    this.textarea.style.fontFamily = "monospace";
    this.textarea.style.fontSize = "13px";
    this.textarea.style.resize = "vertical";
    this.textarea.addEventListener("input", () => {
      if (this.textarea) {
        this.data.htmlContent = this.textarea.value;
      }
      this.updatePreview();
    });

    this.preview = document.createElement("div");
    this.preview.style.border = "1px solid #e0e0e0";
    this.preview.style.borderRadius = "6px";
    this.preview.style.padding = "10px";
    this.preview.style.minHeight = "50px";
    this.preview.style.backgroundColor = "#fafafa";

    container.appendChild(this.textarea);
    container.appendChild(this.preview);
    this.wrapper.appendChild(container);

    this.updatePreview();
    return this.wrapper;
  }

  updatePreview() {
    if (!this.preview) return;
    this.preview.innerHTML = this.data.htmlContent || '<span style="color: #9F9F9F;">Preview will show here</span>';
  }

  save() {
    return this.data;
  }
}
